<template>
  <div class="memorial-fx">
    <!-- ====== 全屏暖色氛围光 ====== -->
    <transition name="fx-fade">
      <div class="warm-overlay" :class="{ strong: scene.paper }" v-if="scene.incense || scene.paper"></div>
    </transition>

    <!-- ====== ① 送鲜花：花瓣雨 + 中央花束绽放 ====== -->
    <transition name="fx-fade">
      <div class="scene flower-scene" v-if="scene.flower">
        <div
            v-for="p in petals"
            :key="p.id"
            class="petal"
            :style="{
              left: p.left + '%',
              animationDuration: p.duration + 's',
              animationDelay: p.delay + 's',
              fontSize: p.size + 'px'
            }"
        >{{ p.emoji }}</div>
        <transition name="bloom-pop">
          <div class="bouquet" v-if="bouquetShow">
            <div class="bouquet-glow"></div>
            <i class="light-ray" v-for="n in 8" :key="n" :style="{ transform: 'rotate(' + n * 45 + 'deg)' }"></i>
            <div class="bouquet-emoji">💐</div>
          </div>
        </transition>
      </div>
    </transition>

    <!-- ====== ② 点香：三炷香升起 + 青烟（配合背景图香炉） ====== -->
    <transition name="fx-fade">
      <div class="scene incense-scene" v-if="scene.incense">
        <div class="incense-center" :style="{ bottom: incenseBottom }">
          <div class="smoke-box">
            <div
                v-for="s in smokes"
                :key="s.id"
                class="smoke"
                :style="{ left: s.x + 'px', transform: 'scale(' + s.scale + ')' }"
            ></div>
          </div>
          <div class="incense-sticks">
            <i class="stick-wrap w1"><b class="stick"><em class="ember"></em></b></i>
            <i class="stick-wrap w2"><b class="stick"><em class="ember"></em></b></i>
            <i class="stick-wrap w3"><b class="stick"><em class="ember"></em></b></i>
          </div>
          <div class="soft-glow"></div>
        </div>
      </div>
    </transition>

    <!-- ====== ③ 烧纸钱：飘落 → 火光乍现 → 化作金光飞升 → 蝴蝶寄思 ====== -->
    <transition name="fx-fade">
      <div class="scene paper-scene" v-if="scene.paper">
        <!-- 飘落燃烧的纸钱（古典样式：铜钱印 / 黄裱纸 交替） -->
        <div
            v-for="p in papers"
            :key="p.id"
            class="burn-paper"
            :style="{ left: p.left + '%', animationDelay: p.delay + 's' }"
        >
          <i class="joss-paper" :class="p.variant" :style="{ animationDelay: p.burnDelay + 's' }"></i>
          <i class="fire-flash" :style="{ animationDelay: p.burnDelay + 's' }"></i>
        </div>

        <!-- 地面燃点：火焰 + 暖光 + 纸灰 -->
        <div class="burn-point" :style="{ bottom: burnBottom }">
          <i class="pt-flame l1"></i>
          <i class="pt-flame l2"></i>
          <i class="pt-flame l3"></i>
          <div class="ember-glow"></div>
          <div
              v-for="a in ashes"
              :key="a.id"
              class="ash-float"
              :style="{ left: a.x + 'px', animationDelay: a.delay + 's' }"
          ></div>
        </div>

        <!-- 化作金光的光点群（螺旋飞升） -->
        <div
            v-for="g in goldMotes"
            :key="'g' + g.id"
            class="gold-mote"
            :style="{
              left: g.left + '%',
              bottom: g.bottom,
              width: g.size + 'px',
              height: g.size + 'px',
              animationDelay: g.delay + 's',
              animationDuration: g.duration + 's',
              '--sway': g.sway + 'px'
            }"
        ></div>

        <!-- 寄思蝴蝶（从燃点起飞） -->
        <div
            v-for="b in butterflies"
            :key="'b' + b.id"
            class="soul-butterfly"
            :style="{ left: b.left + '%', bottom: burnBottom, animationDelay: b.delay + 's', animationDuration: b.duration + 's' }"
        >🦋</div>
      </div>
    </transition>

    <!-- ====== ④ 送祝福：中央金字 + 光柱 + 心愿飘散 ====== -->
    <transition name="fx-fade">
      <div class="scene bless-scene" v-if="scene.blessing">
        <i class="ray" v-for="n in 12" :key="n" :style="{ transform: 'translateX(-50%) rotate(' + n * 30 + 'deg)' }"></i>
        <div class="bless-text">{{ blessingText }}</div>
        <div
            v-for="h in hearts"
            :key="h.id"
            class="float-heart"
            :style="{ left: h.left + '%', animationDelay: h.delay + 's' }"
        >{{ h.emoji }}</div>
      </div>
    </transition>
  </div>
</template>

<script>
import confetti from "canvas-confetti";

let uid = 0;
const FLOWER_EMOJIS = ["🌸", "🌷", "🌼", "🌺", "💮"];
const HEART_EMOJIS = ["💛", "🧡", "✨", "🕯️"];
/** 通用祝福语池：随机取一条，温馨不煽情 */
const BLESSING_POOL = [
  "愿逝者安息 生者坚强",
  "思念常在 音容宛在",
  "清风明月 寄托哀思",
  "愿天堂没有病痛",
  "感恩相伴 永远怀念",
  "点一盏心灯 照亮归途",
  "岁月静好 愿您安眠",
  "花香寄情 思念绵长",
  "纸上千言 皆是想念",
  "愿您在远方 一切安好",
];

export default {
  name: "MemorialAltar",
  props: {
    /** 三炷香的落点（相对屏幕底部），对准背景图中的香炉口 */
    incenseBottom: { type: String, default: "26%" },
    /** 烧纸燃点（相对屏幕底部），地上烧，接近小程序底部 */
    burnBottom: { type: String, default: "12%" },
  },
  data() {
    return {
      scene: { flower: false, incense: false, paper: false, blessing: false },
      // ① 鲜花
      petals: [],
      bouquetShow: false,
      // ② 点香
      smokes: [],
      smokeTimer: null,
      // ③ 烧纸
      papers: [],
      ashes: [],
      goldMotes: [],
      butterflies: [],
      // ④ 祝福
      blessingText: "",
      hearts: [],
      // 定时器收集（beforeDestroy 统一清理）
      timers: [],
    };
  },
  mounted() {
    // 全局事件触发：$Bus.$emit('memorialAction', { type, text })
    this.$Bus.$on("memorialAction", this.onAction);
  },
  beforeDestroy() {
    this.$Bus.$off("memorialAction", this.onAction);
    this.timers.forEach(clearTimeout);
    clearInterval(this.smokeTimer);
  },
  methods: {
    onAction({ type, text } = {}) {
      switch (type) {
        case "flower": this.offerFlower(); break;
        case "incense": this.lightIncense(); break;
        case "paper": this.burnPaperMoney(); break;
        case "blessing": this.sendBlessing(text); break;
      }
    },
    /** 收集定时器，便于统一清理 */
    later(fn, ms) {
      const t = setTimeout(fn, ms);
      this.timers.push(t);
      return t;
    },
    /** 动作完成回调（可用于聊天区插提示、后端上报等） */
    emitDone(action, extra = {}) {
      this.$Bus.$emit("memorialActionDone", { action, ...extra });
    },

    /* ============ ① 送鲜花：全屏花瓣雨 + 中央花束绽放 + 花瓣爆撒 ============ */
    offerFlower() {
      this.scene.flower = true;
      this.bouquetShow = false;
      this.petals = Array.from({ length: 18 }, (_, i) => ({
        id: ++uid,
        emoji: FLOWER_EMOJIS[i % FLOWER_EMOJIS.length],
        left: Math.random() * 100,
        size: 14 + Math.random() * 16,
        duration: 2 + Math.random() * 1.5,
        delay: Math.random() * 1.2,
      }));
      this.later(() => {
        this.bouquetShow = true;
        this.petalBurst();
      }, 2200);
      this.later(() => {
        this.scene.flower = false;
        this.petals = [];
        this.bouquetShow = false;
      }, 5200);
      this.emitDone("flower");
    },
    petalBurst() {
      try {
        const petals = FLOWER_EMOJIS.map(t => confetti.shapeFromText({ text: t, scalar: 2 }));
        confetti({
          shapes: petals,
          particleCount: 36,
          spread: 120,
          startVelocity: 28,
          origin: { y: 0.42 },
          gravity: 0.7,
          drift: 0.8,
          scalar: 1.6,
          ticks: 220,
          zIndex: 9998,
        });
      } catch (e) { /* confetti 失败不影响主流程 */ }
    },

    /* ============ ② 点香：三炷香升起 + 持续青烟 ============ */
    lightIncense() {
      if (this.scene.incense) return; // 已点燃，防重复叠场
      this.scene.incense = true;
      this.smokes = [];
      const spawnSmoke = () => {
        if (!this.scene.incense) return;
        const id = ++uid;
        this.smokes.push({
          id,
          x: -12 + Math.random() * 24,
          scale: 0.7 + Math.random() * 0.8,
        });
        this.later(() => {
          this.smokes = this.smokes.filter(s => s.id !== id);
        }, 5000);
        this.smokeTimer = setTimeout(spawnSmoke, 380);
      };
      spawnSmoke();
      this.later(() => {
        this.scene.incense = false;
        this.smokes = [];
        clearTimeout(this.smokeTimer);
      }, 8000);
      this.emitDone("incense");
    },

    /* ============ ③ 烧纸钱（重制版）============
       t=0     场景开启，地面燃点火焰渐起
       t=0.3起 三批纸钱依次摇曳飘落（每批3张，铜钱印/黄裱纸交替），
               每张落定后燃烧消散 + 火光乍现
       每批燃尽：金色光点螺旋飞升 + confetti 火花迸发
       最后一批：三只蝴蝶从燃点飞起（化蝶寄思）
       全程纸灰袅袅，约 10.5s 收场 */
    burnPaperMoney() {
      this.scene.paper = true;

      // 三批纸钱：0.3s / 2.8s / 5.5s 各一批，每批 3 张
      [300, 2800, 5500].forEach((base, wave) => {
        this.later(() => {
          if (!this.scene.paper) return;
          for (let i = 0; i < 3; i++) {
            const id = ++uid;
            const driftDuration = 2.2 + Math.random() * 0.6;
            this.papers.push({
              id,
              left: 30 + Math.random() * 40,
              delay: i * 0.55,
              burnDelay: driftDuration, // 飘到位后开始燃烧
              variant: (id % 2 === 0) ? "coin" : "seal", // 铜钱印 / 黄裱纸 交替
            });
            this.later(() => {
              this.papers = this.papers.filter(p => p.id !== id);
            }, base + i * 550 + driftDuration * 1000 + 1400);
          }
        }, base);

        // 每批纸钱落定燃烧时：金光飞升 + 火花迸发
        this.later(() => {
          if (!this.scene.paper) return;
          this.spawnGoldMotes(wave === 2 ? 26 : 16); // 最后一批最盛
          this.fireBurst(wave === 2 ? 0.6 : 0.4);
          if (wave === 2) {
            this.spawnButterflies(); // 最后一批：蝴蝶登场
          }
        }, base + 2600);
      });

      // 全程纸灰
      [1000, 3200, 5800, 7600].forEach(base => {
        this.later(() => {
          if (!this.scene.paper) return;
          for (let i = 0; i < 5; i++) {
            const aid = ++uid;
            this.ashes.push({ id: aid, x: -20 + Math.random() * 40, delay: i * 0.4 });
            this.later(() => {
              this.ashes = this.ashes.filter(a => a.id !== aid);
            }, 2800 + i * 400);
          }
        }, base);
      });

      // 收场
      this.later(() => {
        this.scene.paper = false;
        this.papers = [];
        this.ashes = [];
        this.goldMotes = [];
        this.butterflies = [];
      }, 10500);
      this.emitDone("paper");
    },

    /** 金色光点：从燃点螺旋飞升（纸钱化金光的意象） */
    spawnGoldMotes(count) {
      for (let i = 0; i < count; i++) {
        const id = ++uid;
        this.goldMotes.push({
          id,
          left: 42 + Math.random() * 16,
          bottom: this.burnBottom,
          size: 4 + Math.random() * 6,
          delay: i * 0.09 + Math.random() * 0.2,
          duration: 2.4 + Math.random() * 1.4,
          sway: (Math.random() > 0.5 ? 1 : -1) * (30 + Math.random() * 60),
        });
        this.later(() => {
          this.goldMotes = this.goldMotes.filter(g => g.id !== id);
        }, 4500 + i * 90);
      }
    },

    /** 火花迸发（canvas-confetti，橙金色） */
    fireBurst(power) {
      try {
        confetti({
          particleCount: Math.round(28 * power * 2),
          spread: 75,
          startVelocity: 32 * power,
          origin: { y: 0.62 },
          gravity: 0.9,
          decay: 0.9,
          colors: ["#ffd06b", "#ff9d3c", "#ff6b35", "#ffe9b0"],
          scalar: 0.9,
          ticks: 160,
          zIndex: 9998,
        });
      } catch (e) { /* ignore */ }
    },

    /** 寄思蝴蝶：从燃点翩然飞起 */
    spawnButterflies() {
      for (let i = 0; i < 3; i++) {
        const id = ++uid;
        this.butterflies.push({
          id,
          left: 44 + Math.random() * 12,
          delay: i * 0.9,
          duration: 5,
        });
        this.later(() => {
          this.butterflies = this.butterflies.filter(b => b.id !== id);
        }, 6000 + i * 900);
      }
    },

    /* ============ ④ 送祝福：中央金字 + 光柱 + 心愿飘散 ============ */
    sendBlessing(text) {
      this.scene.blessing = true;
      // 不传文案 → 从通用祝福池随机取一条
      this.blessingText = text || BLESSING_POOL[Math.floor(Math.random() * BLESSING_POOL.length)];
      this.hearts = Array.from({ length: 10 }, (_, i) => ({
        id: ++uid,
        emoji: HEART_EMOJIS[i % HEART_EMOJIS.length],
        left: 5 + Math.random() * 90,
        delay: Math.random() * 2.5,
      }));
      try {
        const golds = ["💛", "✨"].map(t => confetti.shapeFromText({ text: t, scalar: 1.8 }));
        confetti({
          shapes: golds,
          particleCount: 30,
          spread: 160,
          startVelocity: 22,
          origin: { y: 0.5 },
          gravity: 0.35,
          scalar: 1.3,
          ticks: 260,
          zIndex: 9998,
        });
      } catch (e) { /* ignore */ }
      this.later(() => {
        this.scene.blessing = false;
        this.hearts = [];
      }, 4500);
      this.emitDone("blessing", { text: this.blessingText });
    },
  },
};
</script>

<style lang="less" scoped>
.memorial-fx {
  position: fixed;
  inset: 0;
  pointer-events: none; // 全程不挡聊天层的滚动/点击
  z-index: 900;

  .scene {
    position: absolute;
    inset: 0;
    overflow: hidden;
  }
}

/* ===== 暖色氛围光 ===== */
.warm-overlay {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 50% 60%,
  rgba(255, 160, 80, 0.14) 0%,
  rgba(255, 130, 60, 0.06) 45%,
  transparent 75%);
  animation: fireFlicker 2.2s ease-in-out infinite alternate;
  &.strong {
    background: radial-gradient(ellipse at 50% 62%,
    rgba(255, 150, 60, 0.26) 0%,
    rgba(255, 110, 40, 0.1) 45%,
    transparent 75%);
    animation-duration: 1.4s;
  }
}
@keyframes fireFlicker {
  0% { opacity: 0.7; }
  100% { opacity: 1; }
}
.fx-fade-enter-active, .fx-fade-leave-active { transition: opacity 0.5s ease; }
.fx-fade-enter, .fx-fade-leave-to { opacity: 0; }

/* ================= ① 鲜花场景 ================= */
.petal {
  position: absolute;
  top: -40px;
  animation: petalFall linear forwards;
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.15));
}
@keyframes petalFall {
  0% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 0; }
  10% { opacity: 1; }
  25% { transform: translateY(20vh) translateX(24px) rotate(120deg); }
  50% { transform: translateY(42vh) translateX(-20px) rotate(240deg); }
  75% { transform: translateY(62vh) translateX(22px) rotate(360deg); }
  100% { transform: translateY(80vh) translateX(0) rotate(420deg); opacity: 0.9; }
}

.bouquet {
  position: absolute;
  top: 38%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  .bouquet-emoji {
    position: relative;
    font-size: 84px;
    filter: drop-shadow(0 0 24px rgba(255, 200, 90, 0.85));
    animation: bouquetSway 2.4s ease-in-out infinite alternate;
  }
  .bouquet-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 220px;
    height: 220px;
    transform: translate(-50%, -50%);
    background: radial-gradient(circle, rgba(255, 215, 130, 0.55) 0%, transparent 70%);
    border-radius: 50%;
    animation: glowPulse 2s ease-in-out infinite alternate;
  }
  .light-ray {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 3px;
    height: 110px;
    transform-origin: top center;
    background: linear-gradient(to bottom, rgba(255, 225, 150, 0.7), transparent);
    border-radius: 2px;
    animation: rayPulse 2s ease-in-out infinite alternate;
  }
}
.bloom-pop-enter-active { transition: all 0.7s cubic-bezier(0.34, 1.56, 0.64, 1); }
.bloom-pop-enter { transform: translate(-50%, -50%) scale(0.2); opacity: 0; }
.bloom-pop-enter-to { transform: translate(-50%, -50%) scale(1); opacity: 1; }

@keyframes bouquetSway {
  0% { transform: rotate(-4deg); }
  100% { transform: rotate(4deg); }
}
@keyframes glowPulse {
  0% { opacity: 0.5; transform: translate(-50%, -50%) scale(0.9); }
  100% { opacity: 1; transform: translate(-50%, -50%) scale(1.15); }
}
@keyframes rayPulse {
  0% { opacity: 0.3; }
  100% { opacity: 0.9; }
}

/* ================= ② 点香场景 =================
   不画香炉！只有三炷香 + 烟，位置用 incenseBottom 对准背景图中的香炉口 */
.incense-scene {
  .incense-center {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    display: flex;
    justify-content: center;

    // 三炷香：外层管"倾斜"，内层管"升起动画"，互不干扰
    .incense-sticks {
      position: relative;
      display: flex;
      align-items: flex-end;
      gap: 16px;
      z-index: 2;

      .stick-wrap {
        display: flex;
        align-items: flex-end;
        &.w1 { transform: rotate(-8deg) translateY(4px); }
        &.w2 { transform: rotate(0deg); }
        &.w3 { transform: rotate(8deg) translateY(4px); }
      }

      .stick {
        position: relative;
        display: block;
        width: 4px;
        height: 70px;
        background: linear-gradient(to top, #6b4526 70%, #2f2010);
        border-radius: 2px;
        box-shadow: 0 0 3px rgba(0, 0, 0, 0.4);
        transform-origin: bottom center;
        animation: stickRise 1s cubic-bezier(0.22, 1, 0.36, 1) backwards;
        .ember {
          position: absolute;
          top: -4px;
          left: 50%;
          transform: translateX(-50%);
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: radial-gradient(circle, #ffd9a0 0%, #ff8542 60%, rgba(255, 100, 30, 0) 100%);
          box-shadow: 0 0 12px 4px rgba(255, 140, 60, 0.75);
          opacity: 0;
          animation: emberOn 0.8s 0.9s ease-out forwards, emberPulse 1.6s 1.7s ease-in-out infinite;
        }
      }
      // 三炷香错落入场，中间最高
      .stick-wrap:nth-child(1) .stick { animation-delay: 0s; }
      .stick-wrap:nth-child(2) .stick { animation-delay: 0.15s; height: 84px; }
      .stick-wrap:nth-child(3) .stick { animation-delay: 0.3s; }
    }

    // 烟：从香头往上飘
    .smoke-box {
      position: absolute;
      bottom: 100%;
      left: 50%;
      width: 0;
      z-index: 1;
      .smoke {
        position: absolute;
        bottom: 10px;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background: radial-gradient(circle,
        rgba(255, 255, 255, 0.45) 0%,
        rgba(235, 235, 235, 0.22) 45%,
        transparent 72%);
        filter: blur(5px);
        animation: smokeBig 5s ease-out forwards;
      }
    }

    // 香炉口的暖光（给背景图里的香炉口"打光"，不是实体）
    .soft-glow {
      position: absolute;
      bottom: -14px;
      left: 50%;
      transform: translateX(-50%);
      width: 130px;
      height: 40px;
      background: radial-gradient(ellipse, rgba(255, 180, 90, 0.35), transparent 70%);
      border-radius: 50%;
      animation: glowPulse 2.4s ease-in-out infinite alternate;
    }
  }
  @keyframes stickRise {
    from { transform: translateY(-60px) scaleY(0.4); opacity: 0; }
    to { transform: translateY(0) scaleY(1); opacity: 1; }
  }
  @keyframes emberOn {
    to { opacity: 1; }
  }
  @keyframes emberPulse {
    0%, 100% { box-shadow: 0 0 8px 3px rgba(255, 140, 60, 0.7); }
    50% { box-shadow: 0 0 16px 6px rgba(255, 180, 80, 0.9); }
  }
  @keyframes smokeBig {
    0% { transform: translateY(0) translateX(0) scale(0.4); opacity: 0; }
    12% { opacity: 0.85; }
    40% { transform: translateY(-110px) translateX(20px) scale(1.6); opacity: 0.6; }
    70% { transform: translateY(-200px) translateX(-16px) scale(2.4); opacity: 0.35; }
    100% { transform: translateY(-300px) translateX(12px) scale(3.2); opacity: 0; }
  }
}

/* ================= ③ 烧纸场景（重制版） ================= */

// ---- 飘落的纸钱 ----
.burn-paper {
  position: absolute;
  top: -12%;
  animation: paperDrift 2.4s ease-in-out forwards;
  z-index: 2;

  // 燃烧瞬间的火光乍现
  .fire-flash {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 60px;
    height: 60px;
    margin: -30px 0 0 -30px;
    border-radius: 50%;
    background: radial-gradient(circle,
    rgba(255, 230, 150, 0.9) 0%,
    rgba(255, 160, 60, 0.5) 40%,
    transparent 70%);
    opacity: 0;
    animation: fireFlash 0.7s ease-out forwards;
    pointer-events: none;
  }
}

// ---- 古典纸钱（CSS 手绘）----
.joss-paper {
  position: relative;
  display: block;
  width: 38px;
  height: 50px;
  border-radius: 3px;
  background: linear-gradient(150deg, #f2d78a 0%, #e2ba5c 45%, #cfa03c 100%);
  box-shadow:
      inset 0 0 8px rgba(140, 95, 20, 0.4),   // 纸的做旧暗角
      inset 0 1px 0 rgba(255, 245, 200, 0.6), // 顶部高光
      0 2px 6px rgba(0, 0, 0, 0.25);
  opacity: 0;
  animation: paperBurn 1.4s ease-out forwards;
  filter: drop-shadow(0 0 6px rgba(255, 160, 60, 0.25));

  // 变体一：铜钱印方孔纸钱（黄纸 + 圆形钱印 + 中间方孔）
  &.coin {
    &::before { // 圆形钱印
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: 24px;
      height: 24px;
      margin: -12px 0 0 -12px;
      border: 2.5px solid rgba(130, 85, 15, 0.6);
      border-radius: 50%;
    }
    &::after { // 方孔
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: 9px;
      height: 9px;
      margin: -4.5px 0 0 -4.5px;
      background: rgba(130, 85, 15, 0.6);
      border-radius: 1px;
    }
  }

  // 变体二：黄裱纸（竖排假文 + 右上角红色印章）
  &.seal {
    background:
        repeating-linear-gradient(to bottom,
        transparent 0, transparent 5px,
        rgba(130, 85, 15, 0.28) 5px, rgba(130, 85, 15, 0.28) 6.5px),
        linear-gradient(150deg, #f0d07e 0%, #deba58 50%, #cc9c3a 100%);
    &::before { // 红色印章
      content: "";
      position: absolute;
      top: 5px;
      right: 5px;
      width: 12px;
      height: 12px;
      background: rgba(190, 45, 35, 0.75);
      border-radius: 2px;
      box-shadow: inset 0 0 3px rgba(120, 20, 10, 0.8);
    }
    &::after { // 底部横线（裱纸折痕）
      content: "";
      position: absolute;
      bottom: 6px;
      left: 6px;
      right: 6px;
      height: 1.5px;
      background: rgba(130, 85, 15, 0.35);
    }
  }
}

// 纸钱飘落轨迹：终点接近屏幕底部（地上烧）
@keyframes paperDrift {
  0% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 0; }
  8% { opacity: 1; }
  18% { transform: translateY(14vh) translateX(22px) rotate(35deg); }
  38% { transform: translateY(30vh) translateX(-18px) rotate(-25deg); }
  58% { transform: translateY(46vh) translateX(20px) rotate(20deg); }
  78% { transform: translateY(60vh) translateX(-8px) rotate(-8deg); opacity: 1; }
  100% { transform: translateY(72vh) translateX(0) rotate(0deg); opacity: 0.95; }
}

// 燃烧消散：泛橙光 → 卷曲 → 模糊化
@keyframes paperBurn {
  0% { opacity: 0; transform: scale(1) rotate(0); }
  15% { opacity: 1; filter: brightness(1) drop-shadow(0 0 6px rgba(255, 160, 60, 0.3)); }
  35% { filter: brightness(1.8) drop-shadow(0 0 18px rgba(255, 150, 50, 0.95)); transform: scale(0.95) rotate(8deg); }
  65% { opacity: 0.7; filter: brightness(1.4) drop-shadow(0 0 12px rgba(255, 110, 40, 0.7)); transform: scale(0.55) rotate(-14deg) translateY(12px); }
  100% { opacity: 0; filter: brightness(1) blur(5px); transform: scale(0.08) translateY(34px); }
}
@keyframes fireFlash {
  0% { opacity: 0; transform: scale(0.2); }
  30% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(1.6); }
}

// ---- 地面燃点：火焰 + 暖光 + 纸灰 ----
.burn-point {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  .ember-glow {
    position: absolute;
    bottom: -24px;
    left: 50%;
    transform: translateX(-50%);
    width: 150px;
    height: 60px;
    background: radial-gradient(ellipse,
    rgba(255, 170, 80, 0.4) 0%,
    rgba(255, 130, 50, 0.18) 45%,
    transparent 75%);
    border-radius: 50%;
    animation: emberGlowBreath 2s ease-in-out infinite alternate;
  }
  .pt-flame {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform-origin: 50% 100%;
    background: radial-gradient(ellipse at 50% 100%,
    #fff3b0 0%, #ffce54 32%, #ff8c3a 62%, rgba(255, 90, 30, 0) 100%);
    border-radius: 50% 50% 50% 50% / 64% 64% 36% 36%;
    filter: blur(1.5px);
    &.l1 { width: 26px; height: 52px; margin-left: -13px; animation: ptFlame 0.42s ease-in-out infinite alternate; }
    &.l2 { width: 18px; height: 38px; margin-left: -30px; animation: ptFlame 0.35s 0.1s ease-in-out infinite alternate; }
    &.l3 { width: 18px; height: 40px; margin-left: 12px; animation: ptFlame 0.48s 0.06s ease-in-out infinite alternate; }
  }
  .ash-float {
    position: absolute;
    bottom: 10px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(190, 182, 170, 0.9);
    opacity: 0;
    animation: ashDrift 2.8s ease-out forwards;
  }
}
@keyframes ptFlame {
  0% { transform: translateX(0) scaleY(1) scaleX(1); opacity: 0.85; }
  100% { transform: translateX(0) scaleY(1.3) scaleX(0.88); opacity: 1; }
}
@keyframes emberGlowBreath {
  0% { opacity: 0.6; transform: translateX(-50%) scale(0.9); }
  100% { opacity: 1; transform: translateX(-50%) scale(1.12); }
}
@keyframes ashDrift {
  0% { transform: translateY(0) translateX(0) rotate(0); opacity: 0; }
  15% { opacity: 0.75; }
  60% { transform: translateY(-100px) translateX(20px) rotate(180deg); opacity: 0.45; }
  100% { transform: translateY(-180px) translateX(-10px) rotate(360deg); opacity: 0; }
}

// ---- 化作金光的光点（螺旋飞升） ----
.gold-mote {
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle,
  #fff7d6 0%, #ffd06b 45%, rgba(255, 160, 60, 0.4) 75%, transparent 100%);
  box-shadow: 0 0 10px 3px rgba(255, 190, 90, 0.75), 0 0 22px 8px rgba(255, 160, 60, 0.3);
  opacity: 0;
  animation: goldSpiral ease-in-out forwards;
}
@keyframes goldSpiral {
  0% { transform: translate(0, 0) scale(0.3); opacity: 0; }
  10% { opacity: 1; transform: translate(calc(var(--sway) * 0.3), -8vh) scale(1); }
  35% { transform: translate(calc(var(--sway) * -0.5), -22vh) scale(0.9); }
  60% { transform: translate(calc(var(--sway) * 0.8), -36vh) scale(0.75); opacity: 0.9; }
  85% { transform: translate(calc(var(--sway) * -0.6), -48vh) scale(0.5); opacity: 0.6; }
  100% { transform: translate(calc(var(--sway) * 0.3), -58vh) scale(0.2); opacity: 0; }
}

// ---- 寄思蝴蝶（从燃点起飞） ----
.soul-butterfly {
  position: absolute;
  font-size: 26px;
  opacity: 0;
  filter: drop-shadow(0 0 8px rgba(255, 200, 120, 0.7));
  animation: butterflyFly ease-in-out forwards;
}
@keyframes butterflyFly {
  0% { transform: translate(0, 0) rotate(-8deg) scale(0.5); opacity: 0; }
  10% { opacity: 1; transform: translate(30px, -6vh) rotate(8deg) scale(1); }
  25% { transform: translate(-24px, -13vh) rotate(-10deg); }
  40% { transform: translate(34px, -20vh) rotate(10deg); }
  55% { transform: translate(-30px, -28vh) rotate(-8deg); }
  70% { transform: translate(26px, -36vh) rotate(8deg); opacity: 0.9; }
  85% { transform: translate(-18px, -43vh) rotate(-6deg); opacity: 0.5; }
  100% { transform: translate(10px, -50vh) rotate(0) scale(0.8); opacity: 0; }
}

/* ================= ④ 祝福场景 ================= */
.bless-scene {
  .ray {
    position: absolute;
    top: 42%;
    left: 50%;
    width: 5px;
    height: 46vh;
    transform-origin: top center;
    background: linear-gradient(to bottom, rgba(255, 220, 140, 0.35), transparent 80%);
    border-radius: 3px;
    animation: rayPulse 2.2s ease-in-out infinite alternate;
  }
  .bless-text {
    position: absolute;
    top: 40%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 16px 34px;
    background: rgba(60, 38, 15, 0.72);
    border: 1px solid rgba(255, 210, 130, 0.6);
    border-radius: 16px;
    font-size: 22px;
    font-weight: bold;
    letter-spacing: 4px;
    color: #ffe3a1;
    text-shadow: 0 0 14px rgba(255, 190, 90, 0.9), 0 2px 4px rgba(0, 0, 0, 0.6);
    box-shadow: 0 0 30px rgba(255, 190, 90, 0.45), inset 0 0 20px rgba(255, 190, 90, 0.12);
    white-space: nowrap;
    animation: blessIn 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) backwards,
    blessBreath 2.4s 0.8s ease-in-out infinite alternate;
  }
  @keyframes blessIn {
    from { transform: translate(-50%, -50%) scale(0.3); opacity: 0; }
    to { transform: translate(-50%, -50%) scale(1); opacity: 1; }
  }
  @keyframes blessBreath {
    0% { text-shadow: 0 0 14px rgba(255, 190, 90, 0.9), 0 2px 4px rgba(0, 0, 0, 0.6); }
    100% { text-shadow: 0 0 26px rgba(255, 210, 120, 1), 0 2px 4px rgba(0, 0, 0, 0.6); }
  }

  .float-heart {
    position: absolute;
    bottom: 10%;
    font-size: 22px;
    opacity: 0;
    animation: heartRise 3s ease-out forwards;
  }
  @keyframes heartRise {
    0% { transform: translateY(0) scale(0.6); opacity: 0; }
    20% { opacity: 0.95; }
    100% { transform: translateY(-46vh) scale(1.15); opacity: 0; }
  }
}
</style>
